import graphql__a17d18b0d71b921f458f from "./__generated__/ContributionsDatavizGraphThemeAnimation_theme.graphql.js";
import graphql__0c6cd64511c24ad380db from "./__generated__/ContributionsDatavizGraphThemeAnimation_contribution.graphql.js";import { Suspense, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import ReactDOMServer from "react-dom/server";
import { graphql, useFragment } from "react-relay";

import d3 from "../../../../lib/d3";
import ContributionsDatavizContributionTooltip from "./ContributionsDatavizContributionTooltip";
import { useNavigateTo } from "../../../Routes/RoutesContext";
import { useResourcesList } from "../../../ResourcesList/resourcesList";
import { BoxSpinner } from "../../../Utils/Spinner";
import { isResourceFilteredIn } from "../../../Utils/resourcesList";
import { useDependenciesDidChange } from "../../../Utils/useDependenciesDidChange";

const ContributionsDatavizGraphThemeAnimation = props => {
  const theme = useFragment(ContributionsDatavizGraphThemeAnimation_theme, props.theme);
  const { participativeModule, slug: themeSlug } = theme;
  const participativeModuleSlug = participativeModule.slug;
  const { contributions } = props;

  const [tooltipContribution, setTooltipContribution] = useState();

  const { search } = useResourcesList();
  const navigateTo = useNavigateTo();

  const showContribution = useCallback(
    ({ slug: contributionSlug }) =>
      navigateTo("datavizGraphThemeContribution", {
        participativeModuleSlug,
        themeSlug,
        contributionSlug
      }),
    [navigateTo, participativeModuleSlug, themeSlug]
  );

  const showTooltip = useCallback((contribution, tooltip, element) => {
    tooltip.show(element);
    setTooltipContribution({ ...contribution, tooltip, element });
  }, []);

  const hideTooltip = useCallback(tooltip => {
    tooltip.hide();
    setTooltipContribution(null);
  }, []);

  // This is a cleanup only useEffect hook to ensure that the tooltip is actually removed
  // before the component is unmounted.
  const contributionTooltip = tooltipContribution?.tooltip;
  useEffect(() => () => contributionTooltip?.hide(), [contributionTooltip]);

  // This function is a ref callback called by the tooltip component which, when the
  // tooltip is rendered, calls the function with its container HTML node as an argument,
  // and allows us to statically copy the generated HTML into the D3tip tooltip content.
  const handleTooltipLoaded = container => {
    if (!container || !tooltipContribution) return;

    tooltipContribution.tooltip.html(container.parentNode.innerHTML);
    tooltipContribution.tooltip.show(tooltipContribution.element);
  };

  const [container, setContainer] = useState();
  const dependenciesDidChange = useDependenciesDidChange();

  useEffect(() => {
    if (!container) return;

    // Double-check that dependencies have actually changed before calling the animate
    // function, as if we don't, there are too many `animate` calls.
    //
    // Note that we don't re-render when the `showContribution` callback changes, as it
    // changes when the `navigateTo` function changes, which means that the route location
    // changed, and implies the opening of the modal. This allows us not to re-animate
    // the graph when opening and closing a modal, which feels more natural when
    // navigating.
    //
    const dependenciesChanged = dependenciesDidChange({
      theme,
      contributions,
      container,
      search,
      showTooltip,
      hideTooltip
    });

    if (!dependenciesChanged) return;

    animate(
      theme,
      contributions,
      container,
      search,
      showContribution,
      showTooltip,
      hideTooltip
    );
  }, [
    theme,
    contributions,
    container,
    search,
    showContribution,
    showTooltip,
    hideTooltip,
    dependenciesDidChange
  ]);

  return (
    <>
      <div data-contributions-dataviz-theme={theme.slug} ref={setContainer}></div>

      {/* The above portal serves as a hidden renderer for the tooltip contents, which
          allows us to asynchronously load the tooltip contribution data, and dramatically
          reduce the loading time of the main view.
      */}
      {tooltipContribution &&
        createPortal(
          <div style={{ display: "none" }}>
            <Suspense fallback={<BoxSpinner />}>
              <ContributionsDatavizContributionTooltip
                theme={theme}
                contributionSlug={tooltipContribution.slug}
                onLoaded={handleTooltipLoaded}
              />
            </Suspense>
          </div>,
          document.body
        )}
    </>
  );
};

// This is the D3 animation function which is extracted from the React component to avoid
// the function to be redefined each time that the component is re-rendered, which would
// also re-animate the dataviz graph when there's no changes to animate as the `animate`
// function would not have the same JS object id and would be treated by useEffect as a
// property change.
//
function animate(
  theme,
  nodes,
  container,
  search,
  showContribution,
  showTooltip,
  hideTooltip
) {
  const getSideLength = d => {
    return (d.r * 2) / Math.sqrt(2);
  };

  const defaultTooltipHTML = ReactDOMServer.renderToString(<BoxSpinner />);

  const tip = d3
    .createTooltip()
    .attr("class", "d3-tip")
    .offset([-15, 0])
    .html(defaultTooltipHTML);

  // For the themes filter, a contribution is considered to be active when either there's
  // no questions for the current theme (there are only 2 theme levels), or if it belongs
  // to one of the selected question filters.
  //
  const isContributionFilteredIn = contribution =>
    isResourceFilteredIn(search, contribution);

  const width = container.getBoundingClientRect().width * 0.95,
    height = width,
    padding = 10;

  let svg;

  /**
   * sélectionne le svg s'il est déjà présent pour éviter d'en créer d'autres après un re-render de react
   **/
  if (!d3.select(container).select("svg").empty()) {
    svg = d3.select(container).select("svg");
  } else {
    svg = d3
      .select(container)
      .append("svg")
      .attr("class", "d-block mx-auto margin-bottom-sm");
  }

  // Resize the SVG depending on the parent container size
  svg.attr("width", width).attr("height", height);

  // Calculates the weight value of a contribution based on the number of reactions that
  // it has.
  const value = ({ reactionsCount }) => 1 + reactionsCount;

  // hiérarchise les contributions et calcul les coordonnées des uns par rapport aux autres pour
  // tenir dans un cercle
  const pack = tree =>
    d3.pack().size([width, height]).padding(padding)(
      d3
        .hierarchy(tree)
        .sum(d => (d.children ? 0 : value(d)))
        .sort((a, b) => b.value - a.value)
    );

  nodes = pack({ children: nodes });
  nodes = nodes.descendants().slice(1);

  // Draws the transparent circle that will be used as the container of all contribution
  // bubbles if it doesn't already exist.
  const mainCircle = !d3.select(container).select(".main").empty()
    ? d3.select(container).select(".main")
    : svg.append("circle").attr("class", "main");

  mainCircle
    .attr("fill", theme.primaryColor)
    .attr("cx", width / 2)
    .attr("cy", height / 2)
    .attr("r", width / 2)
    .style("opacity", 0.2);

  // Selects all possibly pre-existing contribution bubbles and removes them from the DOM
  // and adds the data to create the new ones.
  svg.selectAll(".group").remove();
  let node = svg.selectAll(".group").data(nodes);

  /**
   * transition qui rend les textes des contributions à retirer opaque
   **/
  node.exit().select(".label").transition().style("opacity", 0).duration(500);

  /**
   * transition qui réduit la taille des cercles des contributions à retirer à 0
   **/
  node.exit().select(".sujet").transition().duration(500).attr("r", 0);

  /**
   * transition qui supprime les contributions à retirer
   **/
  node.exit().transition().duration(500).remove();

  const circleGroup = node.enter().append("g").attr("class", "group");
  node = circleGroup.merge(node);

  /**
   * dessine les cercles des nouvelles contributions avec la taille standard
   **/
  circleGroup
    .append("circle")
    .attr("class", "sujet")
    .attr("r", _d => 25)
    .attr("cx", d => d.x)
    .attr("cy", d => d.y)
    .call(tip)
    .on("mouseover", ({ data: contribution }, index, elements) =>
      showTooltip(contribution, tip, elements[index])
    )
    .on("mouseout", () => {
      tip.html(defaultTooltipHTML);
      hideTooltip(tip);
    })
    .on("click", ({ data: contribution, ...d }) => {
      showContribution(contribution);

      // Force tooltip hiding to avoid Chrome bug where the Tooltip stays open when the
      // modal contribution opens
      tip.hide(d);
    });

  /**
   * dessine le texte opaque des nouvelles contributions
   **/
  circleGroup
    .append("foreignObject")
    .attr("class", "label")
    .attr("width", d => getSideLength(d))
    .attr("height", d => getSideLength(d))
    .attr("x", d => d.x)
    .attr("y", d => d.y)
    .style("overflow", "visible")
    .append("xhtml:div")
    .style("opacity", 0);

  /**
   * transition qui agrandit/réduit les cercles selon la valeur des contributions
   * et les rend transparent en fonction des questions sélectionnées
   **/
  node
    .select(".sujet")
    .transition()
    .attr("cx", d => d.x)
    .attr("cy", d => d.y)
    .attr("r", d => d.r)
    .attr("fill", theme.primaryColor)
    .style("opacity", ({ data: contribution }) =>
      isContributionFilteredIn(contribution) ? 1 : 0.25
    )
    .duration(500);

  /**
   * transition qui agrandit/réduit la zone de texte des contributions selon leur valeur
   **/
  node
    .select(".label")
    .transition()
    .attr("width", d => getSideLength(d))
    .attr("height", d => getSideLength(d))
    .attr("x", d => d.x - getSideLength(d) / 2)
    .attr("y", d => d.y - getSideLength(d) / 2)
    .duration(500);

  /**
   * dessine le texte des contributions en fonction de leur valeur
   **/
  node
    .select(".label div")
    .html(({ data: contribution, ...d }, _i) => {
      const sideLength = getSideLength(d);
      const linesCount = Math.floor((sideLength - 15) / 15);
      const displayLabel = sideLength >= 50;

      if (!displayLabel) return "";

      return ReactDOMServer.renderToString(
        <div className="name text-center">
          <div
            style={{
              WebkitLineClamp: linesCount,
              color: theme.primaryContrastColor
            }}
          >
            {contribution.title}
          </div>
        </div>
      );
    })
    .transition()
    .style("opacity", 1)
    .duration(500);
}

const ContributionsDatavizGraphThemeAnimation_theme = graphql__a17d18b0d71b921f458f;

graphql__0c6cd64511c24ad380db;

export default ContributionsDatavizGraphThemeAnimation;
